/**
 * Workspace-scoped replacement for `keepPreviousData`.
 *
 * `keepPreviousData` reuses the previous OBSERVER result for any key change,
 * including a change of workspace. Nothing in the Insights route forces a
 * remount when the `[workspaceId]` segment changes — there is no
 * `key={workspaceId}` on the layout or page — so the same observer survives a
 * workspace switch. Unconditional reuse would therefore render workspace A's
 * aggregates inside workspace B until B's request resolved: a cross-tenant
 * disclosure, not merely a stale number.
 *
 * This keeps the "don't blank the page on a filter change" behaviour while
 * dropping the placeholder the moment the workspace differs, so a workspace
 * switch still falls back to skeletons — which is the correct UX there anyway.
 *
 * Every migrated query key is either `[name, workspaceId, ...]` or carries the
 * id on a params object at the same position. Keys that match neither shape
 * return null and are treated as non-matching: refusing to reuse is always the
 * safe direction.
 */

function hasWorkspaceId(value: object): value is { workspaceId: unknown } {
  return 'workspaceId' in value
}

function workspaceIdFromKey(
  queryKey: readonly unknown[],
  workspaceKeyIndex: number
): string | null {
  const candidate = queryKey[workspaceKeyIndex]
  if (typeof candidate === 'string') {
    return candidate
  }
  if (typeof candidate === 'object' && candidate !== null && hasWorkspaceId(candidate)) {
    return typeof candidate.workspaceId === 'string' ? candidate.workspaceId : null
  }
  return null
}

/**
 * The previous query is typed structurally rather than as TanStack's `Query`:
 * only `queryKey` is read here, and a narrower parameter type would not accept
 * the concrete `Query<TData, ...>` each factory instantiates.
 */
export function keepPreviousWorkspaceData<TData>(workspaceId: string, workspaceKeyIndex = 1) {
  return <TQuery extends { queryKey: readonly unknown[] }>(
    previous: TData | undefined,
    previousQuery: TQuery | undefined
  ): TData | undefined => {
    if (previous === undefined || previousQuery === undefined) {
      return undefined
    }
    return workspaceIdFromKey(previousQuery.queryKey, workspaceKeyIndex) === workspaceId
      ? previous
      : undefined
  }
}

export function keepPreviousWorkspaceDataOrInitial<TData>(
  workspaceId: string,
  initialData: TData | undefined
) {
  const keepPrevious = keepPreviousWorkspaceData<TData>(workspaceId)
  return <TQuery extends { queryKey: readonly unknown[] }>(
    previous: TData | undefined,
    previousQuery: TQuery | undefined
  ): TData | undefined => {
    const previousWorkspaceData = keepPrevious(previous, previousQuery)
    return previousWorkspaceData === undefined ? initialData : previousWorkspaceData
  }
}
